import ISceneObject from "../../model/sceneObject/ISceneObject";
import PropertyList from "../../model/properties/PropertyList";
import PropertyItem from "../../model/properties/PropertyItem";
import { useServicesState } from "../../store/servicesStateStore";
import { emitter } from "../../input/AppEvents";

// Query pipeline (spec §6): gather each object's own properties, then reduce to the common
// set across the whole selection, flagging varying values. Always called with a single-element
// array today (SelectCmd only tracks one selection), but the merge already generalizes to N.
export function queryProperties(sceneObjects: ISceneObject[]): PropertyList {
    const lists = sceneObjects.map(sceneObject => {
        const list = new PropertyList();
        sceneObject.getProperties(list);
        return list;
    });

    const [first, ...rest] = lists;
    const merged = first ?? new PropertyList();
    for (const list of rest) {
        merged.merge(list);
    }

    return merged;
}

// Edit pipeline (spec §7): parse/validate through the item's translator, fan the new value out
// to every target's real fields (+ immediate local visual refresh via ISceneObject.setProperties),
// then persist each target the same way GeometryCommand.updateEntity already does.
export async function editProperty(sceneObjects: ISceneObject[], item: PropertyItem<any>, displayText: string): Promise<boolean> {
    if (!item.setDisplayValue(displayText)) {
        return false;
    }

    const list = new PropertyList();
    list.addProperty(item);

    for (const sceneObject of sceneObjects) {
        sceneObject.setProperties(list);
    }

    const dataProvider = useServicesState.getState().dataProvider;
    const results = await Promise.all(sceneObjects.map(async sceneObject => {
        const result = await dataProvider?.updateSceneObject(sceneObject.data.uuid, sceneObject.data.clone());
        if (result) {
            sceneObject.geometry.update(sceneObject.data, true);
            // Same signal GeometryMoveEditCmd emits after a handle-drag commits — lets SelectCmd
            // refresh its (otherwise stale) selected-indicator overlay for this object.
            emitter.emit('editCommandCommit', { ownerUuid: sceneObject.data.uuid });
        }
        return result != null;
    }));

    return results.every(Boolean);
}
